import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Bell, Building, ClipboardList, Package, ShoppingCart, Truck, Wrench } from "lucide-react";

import { inventoryService } from "../services/inventoryService";
import { financialService } from "../services/financialService";
import { usePermissions } from "../hooks/usePermissions";
import { AlertsPanel, HistoryPanel } from "../components/inventory/ActivityPanels";
import { AssetMaintenancePanel } from "../components/inventory/AssetMaintenancePanel";
import { AssetsPanel } from "../components/inventory/AssetsPanel";
import { CategoriesModal } from "../components/inventory/InventoryParts";
import { OrdersPanel } from "../components/inventory/OrdersPanel";
import { StockPanel } from "../components/inventory/StockPanel";
import { SuppliersPanel } from "../components/inventory/SuppliersPanel";
import { isGone, isOpenOrder, money, needsReorder, stockValue, type OrderLine } from "../components/inventory/inventoryLogic";

type Tab = "stock" | "orders" | "suppliers" | "history" | "alerts" | "assets" | "maintenance";
const TABS: { id: Tab; label: string; icon: typeof Package }[] = [
  { id: "stock", label: "Stock", icon: Package },
  { id: "orders", label: "Purchase orders", icon: ShoppingCart },
  { id: "suppliers", label: "Suppliers", icon: Truck },
  { id: "history", label: "Stock history", icon: ClipboardList },
  { id: "alerts", label: "Alerts", icon: Bell },
  { id: "assets", label: "Assets", icon: Building },
  { id: "maintenance", label: "Asset maintenance", icon: Wrench },
];
const KEYS = ["inventory-items", "inventory-categories", "inventory-suppliers", "inventory-orders", "inventory-movements", "inventory-alerts", "inventory-assets", "inventory-maintenance", "asset-status-history"];

/** The stores and the fixed-asset register. The CFO, who posts assets to the ledger, sees the assets alone. */
export default function Inventory() {
  const { hasCapability } = usePermissions();
  // The inventory manager keeps the stores and the register; leadership approves orders;
  // whoever posts to the ledger capitalises, depreciates and disposes of assets.
  const keepsRegister = hasCapability("access.inventory.manager");
  const canApprove = hasCapability("access.inventory.leadership");
  const postsAssets = hasCapability("financial.post_transactions");
  const tabs = TABS.filter((item) => keepsRegister || item.id === "assets");
  const [params, setParams] = useSearchParams();
  const fallback: Tab = keepsRegister ? "stock" : "assets";
  const tab: Tab = tabs.some((item) => item.id === params.get("tab")) ? (params.get("tab") as Tab) : fallback;
  const select = (next: Tab) => setParams(next === fallback ? {} : { tab: next }, { replace: true });
  const [categoriesOpen, setCategoriesOpen] = useState(false);
  const [draft, setDraft] = useState<OrderLine[] | null>(null);

  const queryClient = useQueryClient();
  const items = useQuery({ queryKey: ["inventory-items"], queryFn: inventoryService.getInventoryItems, enabled: keepsRegister });
  const categories = useQuery({ queryKey: ["inventory-categories"], queryFn: inventoryService.getItemCategories, enabled: keepsRegister });
  const suppliers = useQuery({ queryKey: ["inventory-suppliers"], queryFn: inventoryService.getSuppliers, enabled: keepsRegister });
  const orders = useQuery({ queryKey: ["inventory-orders"], queryFn: inventoryService.getPurchaseOrders, enabled: keepsRegister });
  const movements = useQuery({ queryKey: ["inventory-movements"], queryFn: inventoryService.getStockMovements, enabled: keepsRegister && tab === "history" });
  const alerts = useQuery({ queryKey: ["inventory-alerts"], queryFn: inventoryService.getAlerts, enabled: keepsRegister });
  const assets = useQuery({ queryKey: ["inventory-assets"], queryFn: inventoryService.getAssets, enabled: keepsRegister || postsAssets });
  const maintenance = useQuery({ queryKey: ["inventory-maintenance"], queryFn: inventoryService.getMaintenanceRecords, enabled: keepsRegister });
  // Only someone who can post an asset to the ledger picks the accounts it touches.
  const accounts = useQuery({ queryKey: ["inventory-finance-accounts"], queryFn: financialService.getAccounts, enabled: postsAssets });
  const refresh = () => Promise.all(KEYS.map((key) => queryClient.invalidateQueries({ queryKey: [key] })));

  const itemList = items.data ?? [];
  const stocked = itemList.filter((row) => row.is_active);
  const reorder = stocked.filter(needsReorder);
  const out = stocked.filter((row) => row.current_stock <= 0).length;
  const value = stocked.reduce((sum, row) => sum + stockValue(row), 0);
  const openOrders = (orders.data ?? []).filter(isOpenOrder);
  const awaiting = openOrders.filter((row) => row.status === "pending").length;
  const held = (assets.data ?? []).filter((row) => !isGone(row));
  const bookValue = held.reduce((sum, row) => sum + Number(row.net_book_value), 0);
  const unposted = held.filter((row) => !row.is_capitalized).length;
  const newAlerts = (alerts.data ?? []).filter((row) => !row.is_read).length;
  const dash = (query: { isLoading: boolean }, text: string) => (query.isLoading ? "–" : text);

  const tiles: { label: string; value: string; note: string; tab: Tab; icon: typeof Package; alert?: boolean }[] = keepsRegister
    ? [
        { label: "Stock value", value: dash(items, money(value) || "UGX 0"), note: `${stocked.length} items stocked`, tab: "stock", icon: Package },
        { label: "To reorder", value: dash(items, String(reorder.length)), note: out ? `${out} out of stock` : "Nothing has run out", tab: "stock", icon: Bell, alert: reorder.length > 0 },
        { label: "Open orders", value: dash(orders, String(openOrders.length)), note: awaiting ? `${awaiting} waiting for approval` : "None waiting for approval", tab: "orders", icon: ShoppingCart, alert: canApprove && awaiting > 0 },
        { label: "Assets held", value: dash(assets, String(held.length)), note: `Book value ${money(bookValue) || "UGX 0"}`, tab: "assets", icon: Building },
      ]
    : [
        { label: "Assets held", value: dash(assets, String(held.length)), note: `Book value ${money(bookValue) || "UGX 0"}`, tab: "assets", icon: Building },
        { label: "Not on the ledger", value: dash(assets, String(unposted)), note: unposted ? "Registered, waiting to be posted" : "Everything is posted", tab: "assets", icon: Building, alert: unposted > 0 },
      ];
  const badges: Partial<Record<Tab, number>> = { stock: reorder.length, orders: canApprove ? awaiting : 0, alerts: newAlerts };
  const usage = new Map<number, number>();
  for (const row of [...itemList, ...(assets.data ?? [])]) usage.set(row.category, (usage.get(row.category) ?? 0) + 1);
  const startOrder = (lines: OrderLine[]) => {
    setDraft(lines);
    select("orders");
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">{keepsRegister ? "Inventory" : "Fixed assets"}</h1>
        <p className="mt-1 text-sm text-gray-500">
          {keepsRegister
            ? "What the stores hold and what to reorder, buying from suppliers, and the register of the school's assets."
            : "Put registered assets on the ledger, post their depreciation and record disposals."}
        </p>
      </div>

      <div className={`grid gap-3 sm:grid-cols-2 ${tiles.length > 2 ? "lg:grid-cols-4" : ""}`}>
        {tiles.map((tile) => (
          <button
            key={tile.label}
            type="button"
            onClick={() => select(tile.tab)}
            className="flex items-center gap-4 rounded-xl border border-gray-200 bg-white p-4 text-left transition-colors hover:border-primary-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
          >
            <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${tile.alert ? "bg-amber-50 text-amber-700" : "bg-primary-50 text-primary-700"}`}>
              <tile.icon className="h-5 w-5" />
            </span>
            <span className="min-w-0">
              <span className="block text-xs font-medium text-gray-500">{tile.label}</span>
              <span className={`block text-xl font-semibold tabular-nums ${tile.alert ? "text-amber-800" : "text-gray-900"}`}>{tile.value}</span>
              <span className="block truncate text-xs text-gray-500">{tile.note}</span>
            </span>
          </button>
        ))}
      </div>

      {tabs.length > 1 && (
        <nav role="tablist" aria-label="Inventory sections" className="-mb-px flex gap-x-6 overflow-x-auto border-b border-gray-200">
          {tabs.map((item) => {
            const active = tab === item.id;
            const badge = badges[item.id];
            return (
              <button
                key={item.id}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => select(item.id)}
                className={`inline-flex shrink-0 items-center gap-2 border-b-2 px-1 py-2.5 text-sm font-medium ${
                  active ? "border-primary-600 text-primary-700" : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
                }`}
              >
                <item.icon className="h-4 w-4" />
                {item.label}
                {badge ? <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-xs tabular-nums text-amber-800">{badge}</span> : null}
              </button>
            );
          })}
        </nav>
      )}

      {tab === "stock" && keepsRegister && (
        <StockPanel
          items={items.data}
          categories={categories.data ?? []}
          isLoading={items.isLoading}
          isError={items.isError}
          onRetry={() => items.refetch()}
          onChanged={refresh}
          onOpenCategories={() => setCategoriesOpen(true)}
          onReorder={startOrder}
        />
      )}
      {tab === "orders" && keepsRegister && (
        <OrdersPanel
          orders={orders.data}
          suppliers={suppliers.data ?? []}
          items={itemList}
          isLoading={orders.isLoading}
          isError={orders.isError}
          onRetry={() => orders.refetch()}
          canApprove={canApprove}
          onChanged={refresh}
          draft={draft}
          onDraftDone={() => setDraft(null)}
        />
      )}
      {tab === "suppliers" && keepsRegister && (
        <SuppliersPanel suppliers={suppliers.data} orders={orders.data ?? []} isLoading={suppliers.isLoading} isError={suppliers.isError} onRetry={() => suppliers.refetch()} onChanged={refresh} />
      )}
      {tab === "history" && keepsRegister && <HistoryPanel movements={movements.data} isLoading={movements.isLoading} isError={movements.isError} onRetry={() => movements.refetch()} />}
      {tab === "alerts" && keepsRegister && (
        <AlertsPanel alerts={alerts.data} isLoading={alerts.isLoading} isError={alerts.isError} onRetry={() => alerts.refetch()} onChanged={refresh} onOpenItem={() => select("stock")} />
      )}
      {tab === "assets" && (
        <AssetsPanel
          assets={assets.data}
          categories={categories.data ?? []}
          suppliers={suppliers.data ?? []}
          maintenance={maintenance.data ?? []}
          accounts={accounts.data}
          keepsRegister={keepsRegister}
          postsAssets={postsAssets}
          isLoading={assets.isLoading}
          isError={assets.isError}
          onRetry={() => assets.refetch()}
          onChanged={refresh}
          onOpenCategories={() => setCategoriesOpen(true)}
        />
      )}
      {tab === "maintenance" && keepsRegister && (
        <AssetMaintenancePanel records={maintenance.data} assets={assets.data ?? []} isLoading={maintenance.isLoading} isError={maintenance.isError} onRetry={() => maintenance.refetch()} onChanged={refresh} />
      )}

      {categoriesOpen && <CategoriesModal categories={categories.data ?? []} usage={usage} onClose={() => setCategoriesOpen(false)} onChanged={refresh} />}
    </div>
  );
}
